import React, { useState } from 'react';
import { getReportCategoryLabel, getReportStatusLabel } from '../../services/reportService';
import { formatDate, formatDateTime } from '../../utils/dateFormat';
import '../../css/ReportDetailModal.css';

const ReportDetailModal = ({ isOpen, loading, report, onClose, onProcess }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedSanction, setSelectedSanction] = useState('');
  const [sanctionReason, setSanctionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const handleProcess = async () => {
    if (!selectedStatus) return;

    setProcessing(true);
    try {
      await onProcess(
        selectedStatus,
        selectedSanction || null,
        sanctionReason || ''
      );
      setSelectedStatus('');
      setSelectedSanction('');
      setSanctionReason('');
    } finally {
      setProcessing(false);
    }
  };

  const statusOptions = [
    { value: 'REVIEWED', label: '검토됨' },
    { value: 'RESOLVED', label: '처리완료' },
    { value: 'REJECTED', label: '반려' }
  ];

  const sanctionOptions = [
    { value: '', label: '제재 없음' },
    { value: 'WARNING', label: '경고' },
    { value: 'SUSPENSION_3D', label: '3일 정지' },
    { value: 'SUSPENSION_7D', label: '7일 정지' },
    { value: 'SUSPENSION_30D', label: '30일 정지' },
    { value: 'BAN', label: '영구 정지' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content report-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>신고 상세</h2>
          <button className="close-btn" onClick={onClose} aria-label="닫기">×</button>
        </div>

        {loading ? (
          <div className="modal-loading">로딩 중...</div>
        ) : report ? (
          <div className="modal-body">
            <div className="detail-section">
              <h3>기본 정보</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">상태</span>
                  <span className={`status-badge ${report.status === 'PENDING' ? 'status-pending' : report.status === 'RESOLVED' ? 'status-approved' : report.status === 'REJECTED' ? 'status-rejected' : 'status-reviewed'}`}>
                    {getReportStatusLabel(report.status)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">신고 유형</span>
                  <span className="value">{getReportCategoryLabel(report.category)}</span>
                </div>
                <div className="detail-item">
                  <span className="label">신고 사유</span>
                  <span className="value">{report.reason}</span>
                </div>
                <div className="detail-item">
                  <span className="label">신고일시</span>
                  <span className="value">{formatDate(report.created_at)}</span>
                </div>
              </div>
              {report.detail && (
                <div className="detail-item full-width">
                  <span className="label">상세 내용</span>
                  <p className="detail-text">{report.detail}</p>
                </div>
              )}
            </div>

            <div className="detail-section">
              <h3>고객 정보</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">이름</span>
                  <span className="value">{report.customer?.name || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="label">전화번호</span>
                  <span className="value">{report.customer?.phone || '-'}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3>예약 정보</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">매장</span>
                  <span className="value">{report.store?.name || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="label">좌석</span>
                  <span className="value">{report.reservation?.seat_number || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="label">이용 시간</span>
                  <span className="value">
                    {report.reservation?.start_time ? formatDateTime(report.reservation.start_time) : '-'} ~ {report.reservation?.end_time ? formatDateTime(report.reservation.end_time) : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">신고자</span>
                  <span className="value">{report.reporter_name || '-'}</span>
                </div>
              </div>
            </div>

            {report.status === 'PENDING' && (
              <div className="detail-section">
                <h3>신고 처리</h3>
                <div className="process-form">
                  <div className="form-group">
                    <label>처리 상태</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="">상태 선택</option>
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {selectedStatus === 'RESOLVED' && (
                    <>
                      <div className="form-group">
                        <label>제재 종류</label>
                        <select
                          value={selectedSanction}
                          onChange={(e) => setSelectedSanction(e.target.value)}
                        >
                          {sanctionOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {selectedSanction && (
                        <div className="form-group">
                          <label>제재 사유</label>
                          <textarea
                            value={sanctionReason}
                            onChange={(e) => setSanctionReason(e.target.value)}
                            placeholder="제재 사유를 입력하세요"
                            rows={3}
                          />
                        </div>
                      )}
                    </>
                  )}

                  <button
                    className="process-btn"
                    onClick={handleProcess}
                    disabled={!selectedStatus || processing}
                  >
                    {processing ? '처리 중...' : '처리하기'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="modal-error">신고 정보를 불러올 수 없습니다.</div>
        )}
      </div>
    </div>
  );
};

export default ReportDetailModal;